import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { z } from "zod";
import {
  ArrowLeft,
  CalendarDays,
  ChevronRight,
  CircleDot,
  Clapperboard,
  GlassWater,
  Instagram,
  Loader2,
  MapPin,
  Mic2,
  Star,
  UtensilsCrossed,
} from "lucide-react";
import badge from "@/assets/plunkets-badge.png";
import heroInterior from "@/assets/hero-interior.jpg";
import { Button } from "@/components/ui/button";
import { useReservationModal } from "@/context/ReservationContext";
import { trackEvent } from "@/lib/analytics";
import { listPublishedMenu } from "@/lib/menu.functions";
import { mergeMenu } from "@/lib/menu-merge";
import { COCKTAIL_CATEGORIES, FOOD_CATEGORIES } from "@/components/MenuListing";

type View = "home" | "food" | "cocktails";

const GOOGLE_URL = "https://g.page/r/CSqsnDUymdSwEAE/review";
const INSTAGRAM_URL = "https://www.instagram.com/plunketspub";

export const Route = createFileRoute("/go")({
  validateSearch: z.object({ tag: z.string().optional(), view: z.string().optional() }),
  head: () => ({
    meta: [
      { title: "Plunkets Pub Dublin — Quick Links & Menus" },
      {
        name: "description",
        content:
          "Open the Plunkets food and cocktail menus, book pool, darts, karaoke or a table, and see what's on in Dublin city centre.",
      },
      { property: "og:title", content: "Plunkets Pub Dublin — Quick Links & Menus" },
      {
        property: "og:description",
        content: "Menus, bookings, events and everything Plunkets in one place.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://plunkets.ie/go" },
      { property: "og:image", content: "https://plunkets.ie/og-default.jpg?v=3" },
      { property: "og:image:alt", content: "Pool tables and Guinness mural at Plunkets Pub Dublin" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: "https://plunkets.ie/og-default.jpg?v=3" },
      { name: "robots", content: "noindex, follow" },
    ],
  }),
  component: GoPage,
});

const actionClass =
  "group flex min-h-20 w-full items-center gap-4 rounded-md border border-border bg-card/85 px-5 py-4 text-left shadow-lg shadow-background/20 transition hover:border-primary/60 hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:scale-[0.99]";

function GoPage() {
  const { tag: rawTag, view: rawView } = Route.useSearch();
  const tag = rawTag?.slice(0, 100) || "untagged";
  const view: View = rawView === "food" || rawView === "cocktails" ? rawView : "home";
  const { open } = useReservationModal();
  const logged = useRef(false);

  useEffect(() => {
    if (logged.current) return;
    logged.current = true;
    trackEvent("quick_links_visit", { tag });
  }, [tag]);

  const act = (name: string, action: () => void) => {
    trackEvent("quick_link_click", { tag, destination: name });
    action();
  };

  if (view !== "home") {
    return <QuickMenu kind={view} tag={tag} />;
  }

  return (
    <main className="relative min-h-screen overflow-hidden bg-background px-5 py-8 text-foreground sm:py-12">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-80 overflow-hidden" aria-hidden="true">
        <img src={heroInterior} alt="" className="h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/45 via-background/80 to-background" />
      </div>

      <div className="relative mx-auto flex w-full max-w-md flex-col items-center">
        <Link
          to="/"
          aria-label="Go to Plunkets homepage"
          className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <img
            src={badge}
            alt="Plunkets Pub Dublin"
            width={112}
            height={112}
            className="h-24 w-24 object-contain drop-shadow-2xl sm:h-28 sm:w-28"
          />
        </Link>
        <p className="mt-5 text-xs font-semibold uppercase tracking-[0.28em] text-primary">
          Middle Abbey Street · Dublin 1
        </p>
        <h1 className="mt-3 text-center font-display text-4xl font-semibold leading-tight">
          Welcome to Plunkets
        </h1>
        <p className="mt-2 text-center text-sm text-muted-foreground">
          One pub. Every kind of night out.
        </p>

        <QuickIndex current="home" tag={tag} />

        <div className="mt-5 flex w-full flex-col gap-3">
          <ExternalAction
            href={GOOGLE_URL}
            icon={<Star />}
            title="Find us on Google"
            subtitle="Directions, details and reviews"
            onClick={() => trackEvent("quick_link_click", { tag, destination: "google" })}
          />
          <MenuAction
            view="food"
            tag={tag}
            icon={<UtensilsCrossed />}
            title="Food Menu"
            subtitle="Breakfast, pub classics, sharing and more"
            onClick={() => trackEvent("quick_link_click", { tag, destination: "food_menu" })}
          />
          <MenuAction
            view="cocktails"
            tag={tag}
            icon={<GlassWater />}
            title="Cocktails Menu"
            subtitle="Signature cocktails · 2 for €18 Mon–Thu"
            onClick={() => trackEvent("quick_link_click", { tag, destination: "cocktails_menu" })}
          />
          <Action
            icon={<CalendarDays />}
            title="Book a Table"
            subtitle="Food, drinks and good times"
            onClick={() => act("book_table", () => open({ bookingType: "food_and_drinks" }))}
          />

          <div className="my-1 flex items-center gap-3" aria-hidden="true">
            <span className="h-px flex-1 bg-border" />
            <span className="text-[10px] font-semibold uppercase tracking-[0.25em] text-muted-foreground">
              Choose your night
            </span>
            <span className="h-px flex-1 bg-border" />
          </div>

          <Action
            icon={<CircleDot />}
            title="Pool Tables"
            subtitle="3 tables · €12 per hour · from 60 minutes"
            onClick={() => act("pool_table", () => open({ bookingType: "pool_table" }))}
          />
          <Action
            icon={<CircleDot />}
            title="Dart Boards"
            subtitle="3 tournament-grade boards · €15 per hour"
            onClick={() => act("darts", () => open({ bookingType: "darts" }))}
          />
          <Action
            icon={<Mic2 />}
            title="Karaoke Terrace"
            subtitle="10,000+ songs and group packages"
            onClick={() => act("karaoke", () => open({ bookingType: "karaoke" }))}
          />
          <InternalAction
            to="/events"
            icon={<Clapperboard />}
            title="What's On"
            subtitle="Live sport, music, karaoke and DJs"
            onClick={() => trackEvent("quick_link_click", { tag, destination: "events" })}
          />
        </div>

        <div className="mt-8 flex items-center gap-5">
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent("quick_link_click", { tag, destination: "instagram" })}
            className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground transition hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Instagram size={16} /> Instagram
          </a>
          <span className="h-4 w-px bg-border" aria-hidden="true" />
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground transition hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <MapPin size={16} /> Full website
          </Link>
        </div>
        <p className="mt-8 pb-4 text-center text-[11px] text-muted-foreground">
          Plunkets Pub · Middle Abbey Street · Dublin 1
        </p>
      </div>
    </main>
  );
}

function Action({ icon, title, subtitle, onClick }: ActionProps) {
  return (
    <Button type="button" variant="ghost" onClick={onClick} className={actionClass}>
      <ActionContent icon={icon} title={title} subtitle={subtitle} />
    </Button>
  );
}

function MenuAction({ view, tag, ...props }: ActionProps & { view: "food" | "cocktails"; tag: string }) {
  return (
    <Link
      to="/go"
      search={{ tag: tag === "untagged" ? undefined : tag, view }}
      onClick={props.onClick}
      className={actionClass}
    >
      <ActionContent {...props} />
    </Link>
  );
}

type ActionProps = { icon: ReactNode; title: string; subtitle: string; onClick: () => void };

function ExternalAction({ href, ...props }: ActionProps & { href: string }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" onClick={props.onClick} className={actionClass}>
      <ActionContent {...props} />
    </a>
  );
}

function InternalAction({ to, ...props }: ActionProps & { to: "/events" }) {
  return (
    <Link to={to} onClick={props.onClick} className={actionClass}>
      <ActionContent {...props} />
    </Link>
  );
}

function ActionContent({ icon, title, subtitle }: Omit<ActionProps, "onClick">) {
  return (
    <>
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-primary/30 bg-primary/10 text-primary [&>svg]:h-5 [&>svg]:w-5">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-display text-xl font-semibold text-foreground">{title}</span>
        <span className="mt-0.5 block whitespace-normal text-xs leading-relaxed text-muted-foreground">{subtitle}</span>
      </span>
      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-primary" />
    </>
  );
}

function QuickIndex({ current, tag }: { current: View; tag: string }) {
  const links: Array<{ view: View; label: string }> = [
    { view: "home", label: "Home" },
    { view: "food", label: "Food" },
    { view: "cocktails", label: "Cocktails" },
  ];

  return (
    <nav aria-label="Navegação rápida" className="sticky top-3 z-40 mt-6 grid w-full grid-cols-3 rounded-md border border-border bg-background/95 p-1 shadow-lg backdrop-blur">
      {links.map((item) => (
        <Link
          key={item.view}
          to="/go"
          search={{ tag: tag === "untagged" ? undefined : tag, view: item.view === "home" ? undefined : item.view }}
          aria-current={current === item.view ? "page" : undefined}
          className={`flex min-h-10 items-center justify-center rounded-sm px-2 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
            current === item.view ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
          }`}
        >
          {item.label}
        </Link>
      ))}
    </nav>
  );
}

function QuickMenu({ kind, tag }: { kind: "food" | "cocktails"; tag: string }) {
  const fetchMenu = useServerFn(listPublishedMenu);
  const { data, isLoading } = useQuery({
    queryKey: ["public-menu"],
    queryFn: () => fetchMenu(),
    staleTime: 60_000,
  });
  const allowed = kind === "food" ? FOOD_CATEGORIES : COCKTAIL_CATEGORIES;
  const items = useMemo(
    () => mergeMenu(data).filter((item) => allowed.includes(item.category)),
    [allowed, data],
  );
  const categories = useMemo(
    () => allowed.filter((category) => items.some((item) => item.category === category)),
    [allowed, items],
  );
  const [active, setActive] = useState<string | null>(null);
  const title = kind === "food" ? "Food Menu" : "Cocktails Menu";

  return (
    <main className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur">
        <div className="mx-auto grid h-16 max-w-2xl grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 px-4">
          <Button asChild variant="ghost" size="icon">
            <Link to="/go" search={{ tag: tag === "untagged" ? undefined : tag }} aria-label="Back to quick links">
            <ArrowLeft />
            </Link>
          </Button>
          <span className="flex min-w-0 items-center gap-2">
            <img src={badge} alt="" className="h-9 w-9 shrink-0 object-contain" />
            <span className="truncate font-display text-xl font-semibold">{title}</span>
          </span>
          <Link to={kind === "food" ? "/food-menu" : "/cocktails"} className="ml-auto text-xs font-semibold text-primary hover:underline">
            Full page
          </Link>
        </div>
        <div className="mx-auto max-w-2xl px-4 pb-3">
          <QuickIndex current={kind} tag={tag} />
        </div>
        {categories.length > 0 && (
          <nav aria-label={`${title} categories`} className="mx-auto flex max-w-2xl snap-x snap-mandatory gap-2 overflow-x-auto px-4 pb-3 [scrollbar-width:none]">
            {categories.map((category) => (
              <a
                key={category}
                href={`#go-${slugify(category)}`}
                onClick={() => setActive(category)}
                className={`shrink-0 snap-start rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                  active === category
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-card text-muted-foreground hover:border-primary/50 hover:text-foreground"
                }`}
              >
                {category}
              </a>
            ))}
          </nav>
        )}
      </header>

      <div className="mx-auto max-w-2xl px-4 py-6">
        {kind === "cocktails" && (
          <div className="mb-6 rounded-md border border-primary/30 bg-primary/10 px-4 py-3 text-center text-sm font-semibold text-primary">
            2 cocktails for €18 · Monday to Thursday
          </div>
        )}
        {isLoading ? (
          <div className="flex justify-center py-16" role="status" aria-label="Loading menu">
            <Loader2 className="animate-spin text-primary" />
          </div>
        ) : items.length === 0 ? (
          <p className="py-16 text-center text-sm text-muted-foreground">No menu items are available right now.</p>
        ) : (
          <div className="space-y-10">
            {categories.map((category) => (
              <section key={category} id={`go-${slugify(category)}`} className="scroll-mt-32">
                <div className="mb-3 flex items-center gap-3">
                  <h2 className="font-display text-2xl font-semibold">{category}</h2>
                  <span className="h-px flex-1 bg-border" />
                </div>
                <ul className="divide-y divide-border">
                  {items
                    .filter((item) => item.category === category)
                    .map((item) => (
                      <li key={item.slug}>
                        <Link
                          to="/menu/$slug"
                          params={{ slug: item.slug }}
                          className="group flex min-h-24 items-center gap-4 py-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          {item.image ? (
                            <img
                              src={item.image}
                              alt={`${item.name} at Plunkets Pub Dublin`}
                              loading="lazy"
                              decoding="async"
                              className="h-20 w-20 shrink-0 rounded-md bg-muted object-cover"
                            />
                          ) : (
                            <span className="flex h-20 w-20 shrink-0 items-center justify-center rounded-md bg-muted text-primary">
                              {kind === "food" ? <UtensilsCrossed /> : <GlassWater />}
                            </span>
                          )}
                          <span className="min-w-0 flex-1">
                            <span className="flex items-start justify-between gap-3">
                              <span className="font-display text-lg font-semibold group-hover:text-primary">{item.name}</span>
                              <span className="shrink-0 text-sm font-semibold text-primary">{item.price}</span>
                            </span>
                            {item.tagline && (
                              <span className="mt-1 line-clamp-2 block text-xs leading-relaxed text-muted-foreground">{item.tagline}</span>
                            )}
                          </span>
                        </Link>
                      </li>
                    ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}

function slugify(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}